/**
 * KPITable —— 列关系/校验/联动的「逻辑层」（纯函数，无 React/ui/api 副作用，可独立单测）。
 *
 * 从 KPITable/index.tsx 的 showColKeys / getColConfig / kpi_code 联动 onChange 搬移而来，
 * 把「依赖」改为入参（isInner / checkCPM / needResultStates / 等），由调用方（KPITable 本体、或
 * ProTableListForm 的 subArray 列）各自注入 —— 一份规则、两处渲染。行为与搬移前逐字节一致，
 * 唯一例外：getColConfig 原为「渲染期 setNeedGetCPM(true) 副作用」，此处改为返回 needGetCPM 标记，
 * 由消费方用 useEffect 触发（避免双宿主重复请求）。
 */

/** 可见列计算入参 */
export interface ShowColKeysInput {
  view: boolean;
  moreKpi?: boolean;
  detailState?: string;
  platforms: string[];
  structuredVersion?: number;
  groupType?: string;
  /** 外层传入的列配置（string 或 { key, show? }） */
  colKeys?: (string | { key: string; show?: boolean })[];
  /** $sType === 'inner' */
  isInner: boolean;
  /** 指标结果类（需要展示 actual_value/result）的状态集合 */
  needResultStates: string[];
}

/** 计算 KPITable 当前应展示的列（自 KPITable/index.tsx:346-403 搬移） */
export function computeShowColKeys(input: ShowColKeysInput): string[] {
  const { view, moreKpi, detailState, platforms, structuredVersion, colKeys = [], isInner, needResultStates = [], groupType } = input;

  const propsColKeys =
    colKeys?.reduce((acc, item) => {
      if (typeof item === 'string') acc.push(item);
      else if (item.show !== false) acc.push(item.key);
      return acc;
    }, []) || [];

  let showColKeys = ['kpi_code', 'expect_value', ...propsColKeys];

  switch (groupType) {
    case 'CD_TRUMPET_GROUP':
      if (platforms.includes('MPC')) {
        showColKeys.push('directKpi');
      }
      if (needResultStates.includes(detailState?.toUpperCase())) {
        showColKeys.push('actual_value', 'result');
      }
      break;

    default:
      if (platforms.includes('MPC')) {
        showColKeys.push('directKpi', 'show_for_out');
        if (structuredVersion >= 1) {
          showColKeys.push('accept_flag', 'accept_remark');
        }
      }
      if (view) {
        showColKeys = showColKeys.filter((item) => item !== 'accept_flag');
      }
      if (['MAKING'].includes(detailState?.toUpperCase())) {
        showColKeys.push('actual_value');
      }
      if (needResultStates.includes(detailState?.toUpperCase())) {
        showColKeys.push('actual_value', 'result');
      }
      if (!isInner) {
        const hideKeys = ['show_for_out', 'result'];
        showColKeys = showColKeys.filter((item) => !hideKeys.includes(item));
      }
      break;
  }

  showColKeys = showColKeys.filter((item) => (colKeys?.find((n: any) => n?.key === item) as any)?.show !== false);

  if (!view || moreKpi) {
    showColKeys.push('do');
  }
  return showColKeys;
}

/** 单列可编辑/必填计算入参 */
export interface ColConfigInput {
  view: boolean;
  need_actual_execution?: boolean;
  moreKpi?: boolean;
  /** 外层列配置（{ key, view?, required?, disabled?, ... }） */
  colKeys?: any[];
  $sType: string;
  product_area?: string;
  /** CPM 判定（来自 useCPMCodeList 的 checkCPM） */
  checkCPM: (code?: string) => boolean;
}

export interface ColConfigResult {
  view: boolean;
  required?: boolean;
  disabled?: boolean;
  [k: string]: unknown;
  /** 该列是否需要触发 CPM 拉取（原渲染期 setNeedGetCPM 副作用 → 消费方 useEffect 触发） */
  needGetCPM?: boolean;
}

/** 计算某列某行的可编辑/必填配置（自 KPITable/index.tsx:405-465 搬移） */
export function computeColConfig(colkey: string, recode: any, input: ColConfigInput): ColConfigResult {
  const { view, need_actual_execution, moreKpi, colKeys = [], $sType, product_area, checkCPM } = input;

  let colConfig: ColConfigResult = { view };

  // 绑定执行数据为否，不能编辑
  if (need_actual_execution === false) {
    return { view: true };
  }

  // 如果是默认指标，且在非默认指标情况下，只能编辑非默认指标
  if (moreKpi && recode?.custom === 1) {
    colConfig.view = false;
    if (colkey === 'show_for_out') {
      colConfig.view = true;
    }
  }

  // 单独配置
  const singleColConfig = colKeys?.find((item: any) => item?.key === colkey) || {};
  colConfig = { ...colConfig, ...singleColConfig };
  delete (colConfig as any).key;

  // 指标结果的编辑逻辑
  if (colkey === 'actual_value' && !colConfig.view) {
    if ($sType === 'outer' && !recode?.show_for_out) {
      colConfig.view = true;
    }
    if ($sType === 'inner' && recode?.show_for_out) {
      colConfig.view = true;
    }
  }

  // 是否达预期的编辑逻辑
  if (colkey === 'result' && !colConfig.view) {
    if (recode?.kpi_schema?.type === 'numeric') {
      colConfig.view = true;
    }
  }
  if (colkey === 'result' && moreKpi) {
    colConfig.view = true;
  }

  // 外包系统 & 产品区域为海外 & 指标类型为CPM可编辑、不通过接口获取
  const OUTER_OVERSEAS = $sType === 'outer' && product_area === 'OVERSEAS';

  // CPM 指标的指标结果不可编辑，无论任何场景
  let needGetCPM = false;
  if (checkCPM(recode?.kpi_schema?.code) && !OUTER_OVERSEAS) {
    if (['actual_value', 'result']?.includes(colkey)) {
      if (colConfig.view === false) {
        needGetCPM = true;
      }
      colConfig.view = true;
    }
  }
  return { ...colConfig, needGetCPM };
}

/** kpi_code 选择后的联动 patch（自 KPITable/index.tsx:644-656 搬移）
 *  选指标 → 清空 expect_value、写入 kpi_code/kpi_schema、按 moreKpi 定 show_for_out */
export function computeKpiCodePatch(
  kpiCode: string,
  kpiList: any[],
  moreKpi?: boolean,
): { expect_value: string; kpi_code: string; kpi_schema: any; show_for_out: boolean } {
  const kpi_schema = kpiList.find((item) => item.code === kpiCode);
  return {
    expect_value: '',
    kpi_code: kpiCode,
    kpi_schema,
    show_for_out: moreKpi ? false : kpi_schema?.show_for_out === 1,
  };
}
